import React, { useRef } from 'react';
import styled from 'styled-components';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { ColorButton } from '../../../ui/ColorButton';
import { FlexScroller } from '../../../ui/Scrollers';
import { ItemPrototype } from './VoterItem';

const ItemEditorFrame = styled.div`
    display: flex;
    flex: 1 1 auto;
    border: solid 1px #d2d6dc;
    border-radius: .25rem;
    resize: none;
`;

const ItemTextInputFrame = styled.div`
    position: relative;
    flex: 1 1 auto;
`;

const ItemTextInput = styled.input`
    position: relative;
    width: 100%;
    border: none;
    line-height: 1.25em;
    font-size: 1rem;
`;

const ItemRow = styled.div`
    display: flex;  
    border-bottom: solid 1px #d2d6dc;
    padding: .125rem .625rem .125rem .25rem;

    &:last-child {
        border-bottom: none;
    }
`;

export const VoterItemSetEditor = ({ items, onChange }: { items: ItemPrototype[], onChange: (next: ItemPrototype[]) => void }) => {

    const frame = useRef<HTMLDivElement>(null);

    const patchItem = (id: string, patch: Partial<ItemPrototype>) => {
        onChange(items.map(x => x.id === id ? { ...x, ...patch } : x));
    };

    const insertItem = (index: number, item: ItemPrototype) => {
        onChange([...items.slice(0, index), item, ...items.slice(index)]);
    };

    const onInputKeyDown = (item: ItemPrototype) => (e: React.KeyboardEvent<HTMLInputElement>) => {
        const offset = (e.target as HTMLInputElement).selectionStart;
        const index = items.indexOf(item);
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const m = e.key === 'ArrowUp' ? -1 : 1;
            const next = items[index + m];
            const input = frame.current?.querySelector(`[data-for="${next?.id}"]`) as HTMLInputElement;
            if (input) {
                input.focus();
                input.setSelectionRange(offset, offset);
            }
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            insertItem(index + 1, { id: uuid(), text: '', color: null });
            // autoFocus will sort this out
        }
        if (e.key === 'Backspace' || e.key === 'Delete') {
            if (item.text === '' && items.length > 1) {
                e.preventDefault();
                const near = items[index - 1] || items[index + 1];
                const nearInput = frame.current?.querySelector(`[data-for="${near?.id}"]`) as HTMLInputElement;
                if (nearInput) {
                    nearInput.focus();
                    nearInput.setSelectionRange(10000, 10000);
                }
                onChange(items.filter(x => x.id !== item.id));
            }
            else if (e.key === 'Delete') {
                const thisInput = frame.current?.querySelector(`[data-for="${item.id}"]`) as HTMLInputElement;
                const next = items[index + 1];
                if (thisInput?.selectionStart === thisInput.value.length && next) {
                    e.preventDefault();
                    onChange(items.filter(x => x.id !== next.id));
                }
            }
        }
    };

    const onInputChange = (item: ItemPrototype) => (e: React.ChangeEvent<HTMLInputElement>) => {
        patchItem(item.id, { text: e.target.value });
    };

    const onColorChange = (item: ItemPrototype) => (color: string) => {
        patchItem(item.id, { color });
    };

    return <ItemEditorFrame ref={frame}>
        <FlexScroller autoHide={false}>
            {items.map(tm =>
                <ItemRow key={tm.id}>
                    <ItemTextInputFrame>
                        <ItemTextInput
                            data-for={tm.id}
                            autoFocus
                            value={tm.text}
                            onChange={onInputChange(tm)}
                            onKeyDown={onInputKeyDown(tm)}
                        />
                    </ItemTextInputFrame>
                    <ColorButton
                        portal
                        allowNull
                        autoSelect
                        groups
                        value={tm.color}
                        onChange={onColorChange(tm)}
                    />
                </ItemRow>
            )}
        </FlexScroller>
    </ItemEditorFrame>;
};
